/**
 * LandingOriginal - Original Landing Page (Archived)
 * This is the original colorful landing page design - archived for reference
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../useAuth';
import SEOHelmet from '../../components/SEOHelmet';

const LandingOriginal: React.FC = () => {
  const { user } = useAuth();

  // NOTE: Removed auto-redirect for SEO purposes
  // Google Search Console flagged homepage as "Page with redirect"
  // Now showing conditional CTA button instead of auto-redirecting

  return (
    <>
      <SEOHelmet
        title="[Archive] CEC Track - Original Landing Page"
        description="Archived original landing page design - not for production use"
        keywords="continuing education credits, CEC tracking, professional license renewal"
        canonical="https://cectrack.com/"
        noindex={true}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CEC Track",
            "alternateName": ["CECTrack", "CEC Tracker"],
            "url": "https://cectrack.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://cectrack.com/cectracklogo.png",
              "contentUrl": "https://cectrack.com/cectracklogo.png",
              "width": "200",
              "height": "113",
              "caption": "CEC Track Logo"
            },
            "image": "https://cectrack.com/cectracklogo.png",
            "description": "Professional continuing education credit tracking platform for architects, engineers, landscape architects, CPAs, nurses, social workers, and other licensed professionals. Manage renewal cycles and stay compliant with state requirements across all 50 states.",
            "foundingDate": "2024",
            "slogan": "Never Miss a Licensure Renewal Again",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Support",
              "email": "support@cectrack.com",
              "url": "https://cectrack.com/contact",
              "availableLanguage": "English"
            },
            "sameAs": [
              "https://github.com/cymatia2/cec-tracker"
            ],
            "offers": {
              "@type": "Offer",
              "name": "CEC Track Free Plan",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free continuing education tracking for professionals"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "CEC Track",
            "url": "https://cectrack.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://cectrack.com/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CEC Track",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "150"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "State License Renewal Guides",
            "description": "Comprehensive CE requirements guides for professional license renewal by state",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "New York License Renewal",
                "url": "https://cectrack.com/ny-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Connecticut License Renewal",
                "url": "https://cectrack.com/ct-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "New Jersey License Renewal",
                "url": "https://cectrack.com/nj-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "California License Renewal",
                "url": "https://cectrack.com/ca-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 5,
                "name": "Washington License Renewal",
                "url": "https://cectrack.com/wa-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 6,
                "name": "Illinois License Renewal",
                "url": "https://cectrack.com/il-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 7,
                "name": "Georgia License Renewal",
                "url": "https://cectrack.com/ga-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 8,
                "name": "Florida License Renewal",
                "url": "https://cectrack.com/fl-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 9,
                "name": "Massachusetts License Renewal",
                "url": "https://cectrack.com/ma-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 10,
                "name": "Pennsylvania License Renewal",
                "url": "https://cectrack.com/pa-license-renewal"
              },
              {
                "@type": "ListItem",
                "position": 11,
                "name": "Texas License Renewal",
                "url": "https://cectrack.com/tx-license-renewal"
              }
            ]
          }
        ]}
      />
      <div style={{ backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '100px 0 80px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 style={{ 
                fontSize: '3.5rem', 
                fontWeight: '700',
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}>
                Never Miss a Licensure Renewal Again
              </h1>
              <p style={{ 
                fontSize: '1.25rem', 
                marginBottom: '2rem',
                opacity: 0.95
              }}>
                Track your Continuing Education Credits across all states in one centralized platform. 
                Stay compliant, organized, and always ready for renewal.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Link 
                  to={user ? "/dashboard" : "/register"}
                  className="btn btn-light btn-lg px-4 py-3"
                  style={{ 
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                  }}
                >
                  {user ? "Go to Dashboard" : "Get Started Free"}
                </Link>
                <Link 
                  to="/how-it-works" 
                  className="btn btn-outline-light btn-lg px-4 py-3"
                  style={{ fontWeight: '600' }}
                >
                  <i className="bi bi-play-circle me-2"></i>
                  See How It Works
                </Link>
              </div>
              <div className="mt-3">
                <Link 
                  to="/login" 
                  className="text-white text-decoration-none"
                  style={{ opacity: 0.9 }}
                >
                  Already have an account? <strong>Sign In →</strong>
                </Link>
              </div>
            </div>
            <div className="col-lg-6 d-none d-lg-block">
              <div 
                style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  padding: '40px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <div className="mb-4">
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    Multi-State License Tracking
                  </h4>
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    Secure Certificate Storage
                  </h4>
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    Automated Compliance Tracking
                  </h4>
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '0' }}>
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    Deadline Reminders
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Everything You Need to Stay Compliant
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d', maxWidth: '700px', margin: '0 auto' }}>
              Built specifically for attorneys, dentists, CPAs, architects, engineers, and licensed professionals who manage multiple state requirements
            </p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div 
                className="card h-100 border-0 shadow-sm"
                style={{ 
                  transition: 'transform 0.3s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-10px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body text-center p-4">
                  <div 
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem',
                      fontSize: '2rem'
                    }}
                  >
                    📊
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Multi-State Tracking
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: 0 }}>
                    Manage licenses and CEC requirements across multiple states from a single dashboard. 
                    See all your renewal cycles at a glance.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div 
                className="card h-100 border-0 shadow-sm"
                style={{ 
                  transition: 'transform 0.3s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-10px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body text-center p-4">
                  <div 
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem',
                      fontSize: '2rem'
                    }}
                  >
                    📁
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Certificate Storage
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: 0 }}>
                    Securely store all your course certificates in the cloud. Access them anytime, 
                    anywhere, and never lose important documentation.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div 
                className="card h-100 border-0 shadow-sm"
                style={{ 
                  transition: 'transform 0.3s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-10px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body text-center p-4">
                  <div 
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.5rem',
                      fontSize: '2rem'
                    }}
                  >
                    🔔
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Smart Reminders
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: 0 }}>
                    Receive timely notifications about upcoming deadlines and missing credits. 
                    Stay ahead of your renewal requirements effortlessly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={{ padding: '80px 0', backgroundColor: 'white' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              How It Works
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Get started in minutes with our simple 4-step process
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-3">
              <div className="text-center">
                <div 
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}
                >
                  1
                </div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Add Your Licenses
                </h4>
                <p style={{ color: '#6c757d' }}>
                  Enter your professional licenses and state requirements
                </p>
              </div>
            </div>

            <div className="col-md-3">
              <div className="text-center">
                <div 
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}
                >
                  2
                </div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Upload Classes
                </h4>
                <p style={{ color: '#6c757d' }}>
                  Add completed and planned CEC classes with certificates
                </p>
              </div>
            </div>

            <div className="col-md-3">
              <div className="text-center">
                <div 
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}
                >
                  3
                </div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Track Progress
                </h4>
                <p style={{ color: '#6c757d' }}>
                  Monitor compliance status across all your renewal cycles
                </p>
              </div>
            </div>

            <div className="col-md-3">
              <div className="text-center">
                <div 
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}
                >
                  4
                </div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Stay Compliant
                </h4>
                <p style={{ color: '#6c757d' }}>
                  Receive reminders and never miss a renewal deadline
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Supported Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Built for Licensed Professionals
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Supporting multiple professions across all 50 states
            </p>
          </div>

          <div className="row justify-content-center g-4">
            {/* Row 1 - Legal & Healthcare */}
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚖️</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Attorneys</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🦷</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Dentists</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💼</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>CPAs</h5>
              </div>
            </div>

            {/* Row 2 - Design & Engineering */}
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏛️</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Architects</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚙️</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Engineers</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌳</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Landscape Architects</h5>
              </div>
            </div>

            {/* Row 3 - Real Estate & Healthcare */}
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏠</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Real Estate Agents</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏥</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Registered Nurses</h5>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center p-4">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🦷</div>
                <h5 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: 0 }}>Dental Hygienists</h5>
              </div>
            </div>
          </div>

          {/* Additional professions message */}
          <div className="row mt-4">
            <div className="col-12">
              <p style={{ fontSize: '1rem', color: '#6c757d', textAlign: 'center', marginTop: '1rem' }}>
                ...and <strong>30+ more professions</strong> including Social Workers, Physical Therapists, 
                Counselors, Appraisers, Land Surveyors, and other licensed professionals across all 50 states.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* State-Specific Landing Pages Section */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              State-Specific CE Requirements
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Comprehensive guides for professional license renewal in your state
            </p>
            <p style={{ fontSize: '1rem', color: '#495057', marginTop: '0.5rem' }}>
              Now covering <strong>11 states</strong> with detailed requirements for <strong>30+ professions</strong>
            </p>
          </div>

          <div className="row g-4 justify-content-center">
            {/* New York */}
            <div className="col-lg-4 col-md-6">
              <Link to="/ny-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗽</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#1e3a8a' }}>
                      New York
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>13 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      Architects, Engineers, CPAs, Nurses, Social Workers, Real Estate, Appraisers, Dentists
                    </p>
                    <div style={{ color: '#3b82f6', fontWeight: '600' }}>
                      View NY Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Connecticut */}
            <div className="col-lg-4 col-md-6">
              <Link to="/ct-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌲</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#0c4a6e' }}>
                      Connecticut
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>14 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      Nurses, CPAs, Counselors, Therapists, Social Workers, Engineers, Physicians, Dentists
                    </p>
                    <div style={{ color: '#0284c7', fontWeight: '600' }}>
                      View CT Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* New Jersey */}
            <div className="col-lg-4 col-md-6">
              <Link to="/nj-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏖️</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#4c1d95' }}>
                      New Jersey
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>15 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      Architects, Engineers, Nurses, Social Workers, CPAs, Counselors, Real Estate, Surveyors, Dentists
                    </p>
                    <div style={{ color: '#7c3aed', fontWeight: '600' }}>
                      View NJ Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* California */}
            <div className="col-lg-4 col-md-6">
              <Link to="/ca-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>☀️</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#d97706' }}>
                      California
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>5 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, RNs, APRNs, Clinical Social Workers, Attorneys
                    </p>
                    <div style={{ color: '#f59e0b', fontWeight: '600' }}>
                      View CA Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Washington */}
            <div className="col-lg-4 col-md-6">
              <Link to="/wa-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌲</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#065f46' }}>
                      Washington
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>9 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, RNs, LPNs, APRNs, Social Workers, Landscape Architects, Attorneys
                    </p>
                    <div style={{ color: '#059669', fontWeight: '600' }}>
                      View WA Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Illinois */}
            <div className="col-lg-4 col-md-6">
              <Link to="/il-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏙️</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#1e40af' }}>
                      Illinois
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>5 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, Social Workers (LSW/LCSW), Landscape Architects, Attorneys
                    </p>
                    <div style={{ color: '#2563eb', fontWeight: '600' }}>
                      View IL Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Georgia */}
            <div className="col-lg-4 col-md-6">
              <Link to="/ga-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🍑</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#991b1b' }}>
                      Georgia
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>5 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, RNs, LPNs, Clinical Social Workers, Attorneys
                    </p>
                    <div style={{ color: '#dc2626', fontWeight: '600' }}>
                      View GA Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Florida */}
            <div className="col-lg-4 col-md-6">
              <Link to="/fl-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌴</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#ea580c' }}>
                      Florida
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>7 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, RNs, LPNs, Social Workers (LCSW/CMSW), Landscape Architects, Attorneys
                    </p>
                    <div style={{ color: '#f97316', fontWeight: '600' }}>
                      View FL Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Massachusetts */}
            <div className="col-lg-4 col-md-6">
              <Link to="/ma-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚓</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#1e40af' }}>
                      Massachusetts
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>6 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, Social Workers (Multiple Levels), Attorneys
                    </p>
                    <div style={{ color: '#2563eb', fontWeight: '600' }}>
                      View MA Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Pennsylvania */}
            <div className="col-lg-4 col-md-6">
              <Link to="/pa-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔔</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#1e40af' }}>
                      Pennsylvania
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>5 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      CPAs, RNs, Social Workers (LSW/LCSW), Attorneys
                    </p>
                    <div style={{ color: '#2563eb', fontWeight: '600' }}>
                      View PA Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Texas */}
            <div className="col-lg-4 col-md-6">
              <Link to="/tx-license-renewal" style={{ textDecoration: 'none' }}>
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4 text-center">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤠</div>
                    <h5 style={{ fontSize: '1.4rem', fontWeight: '600', marginBottom: '1rem', color: '#991b1b' }}>
                      Texas
                    </h5>
                    <p style={{ color: '#6c757d', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      <strong>8 professions</strong>
                    </p>
                    <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      Architects, CPAs, RNs, LVNs, Social Workers (Multiple Levels)
                    </p>
                    <div style={{ color: '#dc2626', fontWeight: '600' }}>
                      View TX Requirements →
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Coming Soon Section */}
          <div className="text-center mt-5 pt-4">
            <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '1rem' }}>
              <strong>More states coming soon!</strong> Request your state at{' '}
              <Link to="/contact" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '600' }}>
                cectrack.com/contact
              </Link>
            </p>
            <p style={{ fontSize: '0.95rem', color: '#6c757d' }}>
              Expanding coverage to all 50 states with comprehensive CE requirements for all licensed professionals
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your CEC Tracking?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join hundreds of licensed professionals who trust CEC Track to manage their continuing education
          </p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <Link 
              to={user ? "/dashboard" : "/register"}
              className="btn btn-light btn-lg px-5 py-3"
              style={{ 
                fontWeight: '600',
                fontSize: '1.1rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
            >
              {user ? (
                <>
                  Go to Dashboard <i className="bi bi-arrow-right ms-2"></i>
                </>
              ) : (
                <>
                  Get Started Free <i className="bi bi-arrow-right ms-2"></i>
                </>
              )}
            </Link>
            <Link 
              to="/how-it-works" 
              className="btn btn-outline-light btn-lg px-5 py-3"
              style={{ 
                fontWeight: '600',
                fontSize: '1.1rem'
              }}
            >
              <i className="bi bi-play-circle me-2"></i>
              See How It Works
            </Link>
          </div>
          <div className="mt-3">
            <small style={{ opacity: 0.8 }}>Already have an account? <Link to="/login" style={{ color: 'white', textDecoration: 'underline' }}>Sign In</Link></small>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default LandingOriginal;
